import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { Loader2, Palette } from "lucide-react";

interface BrandStylesModalProps {
  open: boolean;
  onClose: () => void;
}

export function BrandStylesModal({ open, onClose }: BrandStylesModalProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [colorScheme, setColorScheme] = useState("");
  const [styleGuidelines, setStyleGuidelines] = useState("");

  useEffect(() => {
    if (open) {
      void loadStyles();
    }
  }, [open]);

  const loadStyles = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/user/styles");
      if (res.ok) {
        const data = await res.json();
        setColorScheme(data.color_scheme || "");
        setStyleGuidelines(data.style_guidelines || "");
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch("/api/user/styles", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ color_scheme: colorScheme, style_guidelines: styleGuidelines }),
      });
      if (res.ok) {
        toast({ title: "Styles saved", description: "Your brand guidelines will be applied to future generations." });
        onClose();
      } else {
        throw new Error("Failed to save styles");
      }
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Could not save styles." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] glass">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5 text-primary" />
            Brand Style Guidelines
          </DialogTitle>
          <DialogDescription>
            Enterprise users can define custom color schemes and style guidelines.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="colors">Color Scheme</Label>
              <Input
                id="colors"
                placeholder="e.g. Primary: #000000, Secondary: #444444, Background: #FFFFFF"
                value={colorScheme}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setColorScheme(e.target.value)}
              />
              <p className="text-[10px] text-muted-foreground">Describe your brand colors or provide hex codes.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="guidelines">Style Guidelines</Label>
              <Textarea
                id="guidelines"
                placeholder="e.g. Use rounded corners, minimalist design, flat icons, thick strokes..."
                className="min-h-[120px]"
                value={styleGuidelines}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setStyleGuidelines(e.target.value)}
              />
              <p className="text-[10px] text-muted-foreground">Specific design rules we should follow when generating your SVGs.</p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving</> : "Save Guidelines"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
